import React, { useCallback, useRef } from "react"
import { Col, Row } from "react-bootstrap"
import { useDrag, useDrop } from "react-dnd"
import { State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { useLanguageState } from "../../globalStates/LanguageState"
import Section from "../../components/Section"
import TextField from "../../components/Field/Text"
import {
    fieldNameByLang,
    getElementFieldValueWithLanguage,
    makeSetHandlerWithLanguage,
    makeValidationHandlerWithLanguage
} from "../../utils/Language"
import { Link } from "../../globalStates/ExhibitorDataTypes"

interface LinkSectionProps {
    linkState: State<Link, Validation>
    index: number
    currentLanguage: string
    onOrderChanged: (productId: string, index: number) => void
    onClickRemove: (productId: string) => void
}

const LinkSection: React.FC<LinkSectionProps> = ({ linkState, index, currentLanguage, onOrderChanged, onClickRemove }) => {
    const branding = useLanguageState().getStrings()
    const link = useHookstate(linkState)
    const sectionRef = useRef<HTMLDivElement>(null)

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(link.id.get())
    }, [onClickRemove, link])

    const [{ isDragging }, drag] = useDrag({
        type: "Link",
        item: { linkId: link.id.get() },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
        end: (item, monitor) => {
            // const { id: droppedId, originalIndex } = item
            const didDrop = monitor.didDrop()
            if (!didDrop) {
            }
        }
    })
    drag(sectionRef)

    const [, drop] = useDrop({
        accept: "Link",
        hover(item) {
            const draggedItem = item as { linkId: string }
            if (draggedItem.linkId !== link.id.get()) {
                onOrderChanged(draggedItem.linkId, index)
            }
        },
        drop(item) {
            const draggedItem = item as { linkId: string }
            onOrderChanged(draggedItem.linkId, index)
        }
    })
    drop(sectionRef)

    return (
        <Section
            id={link.id.get()}
            ref={sectionRef}
            title={
                getElementFieldValueWithLanguage(link, "text", currentLanguage) ||
                branding.configuration.linksPage.sctLink.titleEmptyLink
            }
            opacity={isDragging ? 0 : 1}
            onClickRemove={onClickRemoveHandler}
            entityState={link}
        >
            <Row>
                <Col>
                    <TextField
                        key={fieldNameByLang("url", currentLanguage)}
                        name={fieldNameByLang("url", currentLanguage)}
                        label={branding.configuration.linksPage.sctLink.lblURL}
                        placeholder={branding.configuration.linksPage.sctLink.hntURL}
                        validate={makeValidationHandlerWithLanguage(link, "url", currentLanguage)}
                        value={getElementFieldValueWithLanguage(link, "url", currentLanguage)}
                        onChange={makeSetHandlerWithLanguage(link, "url", currentLanguage)}
                        required
                    />
                </Col>
                <Col>
                    <TextField
                        key={fieldNameByLang("text", currentLanguage)}
                        name={fieldNameByLang("text", currentLanguage)}
                        label={branding.configuration.linksPage.sctLink.lblText}
                        placeholder={branding.configuration.linksPage.sctLink.hntText}
                        validate={makeValidationHandlerWithLanguage(link, "text", currentLanguage)}
                        value={getElementFieldValueWithLanguage(link, "text", currentLanguage)}
                        onChange={makeSetHandlerWithLanguage(link, "text", currentLanguage)}
                        required
                    />
                </Col>
            </Row>
        </Section>
    )
}

export default React.memo(LinkSection)
